import React, {
  useEffect, useCallback, useState, useMemo
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { Select, Option, TextDropdownSelect } from 'barnyard/components';
import { useDebounce } from 'barnyard/hooks';
import { productsSearch } from '../actions';
import { useProductSearchResults, useProductSearchQuery } from '../selectors';


const ProductSelect = (props) => {
  const dispatch = useDispatch();
  const {
    include_all,
    include_any,
    showTextSelect,
    onChange,
    ...other
  } = props;
  const [selected, setSelected] = useState({});
  const [initialValue] = useState(props.value);
  const [searchQuery, setSearchQuery] = useState('');
  const products = useProductSearchResults({ product_ids: initialValue });
  const query = useProductSearchQuery();

  // Debounce the search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Trigger search when debounced query changes
  useEffect(() => {
    if (debouncedSearchQuery) {
      dispatch(productsSearch({ q: debouncedSearchQuery }));
    }
  }, [debouncedSearchQuery, dispatch]);

  // Fetch initial product data on mount
  useEffect(() => {
    if (!_.isEmpty(initialValue)) {
      dispatch(productsSearch({ product_ids: initialValue }));
    }
  }, [initialValue, dispatch]);

  const onChangeValue = useCallback((changeValue) => {
    const selectedValues = _.keyBy(
      _.filter(
        products,
        p => _.includes(changeValue, p.pk)
      ),
      'pk'
    );
    setSelected(currentValue => ({ ...currentValue, ...selectedValues }));
    onChange(changeValue);
  }, [products, onChange]);

  const unselectedProducts = useMemo(() => _.filter(
    products,
    p => !selected[p.pk]
  ), [products, selected]);

  let SelectComponent = Select;

  if (showTextSelect) {
    SelectComponent = TextDropdownSelect;
  }

  return (
    <SelectComponent
      {...other}
      onChange={onChangeValue}
      onSearch={setSearchQuery}
      filterOption={false}
      clearable={true}
      searchPlaceholder='Search Products...'
      notFoundContent={query ? 'No products found' : 'Type to search for a product'}
    >
      {include_all && <Option value='all'>All Products</Option>}
      {include_any && <Option value='any'>Any Product</Option>}
      {
        _.map(
          _.orderBy(selected, 'name'),
          product => (
            <Option value={product.pk} key={product.pk}>{product.name}</Option>
          )
        )
      }
      {_.map(unselectedProducts, product => (
        <Option value={product.pk} key={product.pk}>{product.name}</Option>
      ))}
    </SelectComponent>
  );
};

ProductSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  include_all: PropTypes.bool,
  include_any: PropTypes.bool,
  showTextSelect: PropTypes.bool,
  value: PropTypes.arrayOf(PropTypes.number),
};

export default ProductSelect;
